import { useCallback, useMemo } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import type { CommentMention } from '@ayocore/comments/common/types/comment-mention';
import type { CommentContactMention } from '@ayocore/comments/common/types/comment-contact-mention';
import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import {
  extractActivityScopeFromPayload,
  extractActivityTaskId,
  extractActivityTaskScopeFromPayload,
  type ActivityCommandPayload,
} from '@ayocore/activity/common/types/activity-command-context';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import type { ActivityChatPin } from '@ayocore/activity/common/types/activity-pin';
import type {
  ActivityCommandSource,
  ActivitySlashCommandContext,
} from '../types/activity-slash-command';
import type { ActivityController } from '../controllers/ActivityController';
import { getActivityChatCommandById } from '@ayocore/activity/client/extensions';
import { normalizeActivityScope } from '../utils/normalize-activity-scope';
import { useControllerSelector } from './useControllerSelector';
import { useActivityChatScrollController } from './useActivityChatScrollController';
import { useActivityThreadsViewModel } from './useActivityThreadsViewModel';

/**
 * Controller-aware хук правой панели Activity.
 * Возвращает chat/pins/composer state, действия и viewport-контроллер.
 */
export function useActivityChat(controller: ActivityController) {
  const currentUserId = useAuthController(state => state.user?.id);
  const rootState = useControllerSelector(controller, state => state);
  const chatState = useControllerSelector(controller.chat, state => state);
  const pinsState = useControllerSelector(controller.pins, state => state);

  const {
    displayChatItems,
    chatLoading,
    chatErrorMessage,
    hasNewerMessages,
    canAutoLoadOlder,
  } = useActivityThreadsViewModel({ controller, currentUserId });

  const selectedThreadId = rootState.selectedThreadId;
  const selectedThread = rootState.selectedThread;

  const mentionUsers = useMemo(() => {
    const usersById = new Map<number, { id: number; baseLabel: string }>();
    displayChatItems.forEach(item => {
      if (!item.author_id || item.author_id <= 0) return;
      if (currentUserId && item.author_id === currentUserId) return;
      if (usersById.has(item.author_id)) return;

      const baseLabel =
        typeof item.author_name === 'string' && item.author_name.trim()
          ? item.author_name.trim()
          : `Пользователь #${item.author_id}`;
      usersById.set(item.author_id, {
        id: item.author_id,
        baseLabel,
      });
    });
    const labelCounts = new Map<string, number>();
    usersById.forEach(user => {
      labelCounts.set(user.baseLabel, (labelCounts.get(user.baseLabel) ?? 0) + 1);
    });

    return Array.from(usersById.values()).map(user => {
      const duplicateLabel = (labelCounts.get(user.baseLabel) ?? 0) > 1;
      const label = duplicateLabel ? `${user.baseLabel} #${user.id}` : user.baseLabel;
      return {
        id: user.id,
        label,
        searchText: `${user.baseLabel.toLowerCase()} ${user.id}`,
      };
    });
  }, [displayChatItems, currentUserId]);

  const loadOlderComments = useCallback(() => {
    return controller.chat.loadOlderChat(selectedThreadId);
  }, [controller, selectedThreadId]);

  const reloadChat = useCallback(() => {
    return controller.chat.loadChat(selectedThreadId, { showLoader: false });
  }, [controller, selectedThreadId]);

  const openCommandContext = useCallback(
    (
      commandId: string,
      options: {
        source: ActivityCommandSource;
        event?: ActivityChatItem | null;
        payload?: ActivityCommandPayload | null;
        title?: string;
        subtitle?: string;
      }
    ): boolean => {
      const command = getActivityChatCommandById(commandId);
      if (!command) return false;

      const commandContext: ActivitySlashCommandContext = {
        threadEntityType: selectedThread?.entity_type ?? null,
        threadEntityUuid: selectedThread?.entity_uuid ?? null,
        source: options.source,
        event: options.event ?? null,
        payload: options.payload ?? null,
      };
      if (command.isAvailable && !command.isAvailable(commandContext)) {
        return false;
      }

      const fallbackThreadScope = selectedThread
        ? {
            entity_type: selectedThread.entity_type,
            entity_uuid: selectedThread.entity_uuid,
            ...(selectedThread.entity_label ? { label: selectedThread.entity_label } : {}),
          }
        : null;
      const resolvedScope =
        normalizeActivityScope(options.event?.scope) ??
        extractActivityTaskScopeFromPayload(options.payload ?? null) ??
        extractActivityScopeFromPayload(options.payload ?? null) ??
        fallbackThreadScope;
      if (!resolvedScope) {
        return false;
      }

      controller.chat.openCustomContext({
        slug: command.id,
        title: options.title?.trim() || command.comment?.label?.trim() || command.label,
        subtitle: options.subtitle ?? command.description,
        scope: resolvedScope,
        preview: {
          commandContext,
        },
      });
      return true;
    },
    [controller, selectedThread]
  );

  const handleSendComment = useCallback(
    (
      content: string,
      mentionedUserIds?: number[],
      mentionedUserMentions?: CommentMention[],
      scope?: ActivityCommentScope | null,
      mentionedContactUuids?: string[],
      mentionedContactMentions?: CommentContactMention[],
      fileIds?: number[]
    ): Promise<boolean> => {
      return controller.sendComment(
        content,
        mentionedUserIds,
        mentionedUserMentions,
        scope ?? null,
        mentionedContactUuids,
        mentionedContactMentions,
        fileIds
      );
    },
    [controller]
  );

  const handleMessageAction = useCallback(
    (item: ActivityChatItem, actionId: string): void => {
      if (actionId.startsWith('run_command:')) {
        const commandId = actionId.slice('run_command:'.length).trim();
        if (commandId) {
          openCommandContext(commandId, {
            source: 'comment',
            event: item,
          });
        }
        return;
      }

      const commentUuid = item.comment_uuid?.trim();
      if (!commentUuid) return;

      if (actionId === 'reply_comment') {
        controller.chat.startReply(item);
        setTimeout(() => {
          controller.composer.requestFocus();
        }, 0);
        return;
      }

      if (actionId === 'pin_comment') {
        if (!selectedThreadId) return;
        void controller.pins.pinComment(selectedThreadId, commentUuid).catch((error: unknown) => {
          logger.error('[useActivityChat] pin_comment failed', {
            threadId: selectedThreadId,
            commentUuid,
            error,
          });
        });
        return;
      }

      if (actionId === 'edit_comment') {
        controller.chat.openEditComment(commentUuid, item.content);
        return;
      }

      if (actionId === 'delete_comment') {
        if (!confirm('Удалить комментарий?')) return;
        void controller.chat.deleteComment(commentUuid);
      }
    },
    [controller, selectedThreadId, openCommandContext]
  );

  const handleInsertUserMention = useCallback(
    (userId: number, label: string): void => {
      controller.composer.requestInsertFragment({
        kind: 'user_mention',
        userId,
        label,
      });
    },
    [controller]
  );

  const handleOpenSlashCommand = useCallback(
    (commandId: string): boolean => {
      return openCommandContext(commandId, { source: 'slash' });
    },
    [openCommandContext]
  );

  const handlePinAction = useCallback(
    async (pin: ActivityChatPin, actionId: string): Promise<void> => {
      if (actionId === 'open_comment') {
        const payload = pin.payload ?? {};
        const commentUuidRaw = payload.comment_uuid;
        const commentUuid = typeof commentUuidRaw === 'string' ? commentUuidRaw.trim() : '';
        const anchorEventId = typeof pin.anchor_event_id === 'number' ? pin.anchor_event_id : null;
        if (!commentUuid) return;

        const found = await controller.chat.focusComment(commentUuid, {
          threadId: selectedThreadId,
          anchorEventId,
        });
        if (found) {
          controller.chat.requestJumpToComment(commentUuid);
        }
        return;
      }

      if (actionId === 'unpin_comment') {
        const payload = pin.payload ?? {};
        const commentUuidRaw = payload.comment_uuid;
        const commentUuid = typeof commentUuidRaw === 'string' ? commentUuidRaw.trim() : '';
        if (!selectedThreadId || !commentUuid) return;
        await controller.pins.unpinComment(selectedThreadId, commentUuid);
        return;
      }

      if (actionId === 'reply_task' || actionId === 'open_task_view') {
        openCommandContext('task_view', {
          source: 'pin',
          payload: pin.payload ?? null,
          title: pin.title,
          subtitle: pin.subtitle,
        });
        return;
      }

      const payload = pin.payload ?? null;
      const taskId = extractActivityTaskId(payload);
      if (!taskId) return;

      if (actionId === 'edit_task') {
        openCommandContext('task_edit', {
          source: 'pin',
          payload: pin.payload ?? null,
          title: 'Редактировать задачу',
          subtitle: pin.title,
        });
        return;
      }

      if (actionId === 'complete_task') {
        openCommandContext('task_view', {
          source: 'pin',
          payload: { ...(pin.payload ?? {}), confirm_complete: true },
          title: pin.title,
          subtitle: pin.subtitle,
        });
        return;
      }

      if (actionId === 'delete_task') {
        if (!confirm('Удалить задачу?')) return;
        await controller.chat.deleteTask(taskId);
      }
    },
    [controller, selectedThreadId, openCommandContext]
  );

  const handlePinClick = useCallback(
    (pin: ActivityChatPin) => {
      if (!pin.on_click_action) return;
      void handlePinAction(pin, pin.on_click_action);
    },
    [handlePinAction]
  );

  const setEditingCommentText = useCallback(
    (nextValue: string) => {
      controller.chat.setEditingCommentText(nextValue);
    },
    [controller]
  );

  const cancelReply = useCallback(() => {
    controller.chat.cancelReply();
  }, [controller]);

  const handleJumpToCommentHandled = useCallback(() => {
    controller.chat.handleJumpToCommentHandled();
  }, [controller]);

  const closeEditComment = useCallback(() => {
    controller.chat.closeEditComment();
  }, [controller]);

  const saveEditedComment = useCallback(() => {
    return controller.chat.saveEditedComment();
  }, [controller]);

  const handleScopePresetConsumed = useCallback(() => {
    controller.chat.consumeScopePreset();
  }, [controller]);

  const closeActiveContext = useCallback(() => {
    controller.chat.closeActiveContext();
  }, [controller]);

  const scroll = useActivityChatScrollController({
    selectedThreadId,
    chatItems: displayChatItems,
    chatLoading,
    pinsLoading: pinsState.pinsLoading,
    commentsHasMoreOlder: chatState.commentsHasMoreOlder,
    loadingOlderComments: chatState.loadingOlderComments,
    onLoadOlderComments: () => {
      void loadOlderComments();
    },
    canAutoLoadOlder,
    hasNewerMessages,
    onLoadNewerComments: () => {
      void reloadChat();
    },
    jumpToCommentUuid: chatState.jumpToCommentUuid,
    onJumpToCommentHandled: () => {
      controller.chat.handleJumpToCommentHandled();
    },
  });

  return {
    selectedThread,
    selectedThreadId,
    displayChatItems,
    mentionUsers,
    chatLoading,
    chatErrorMessage,
    hasNewerMessages,
    canAutoLoadOlder,
    commentsHasMoreOlder: chatState.commentsHasMoreOlder,
    loadingOlderComments: chatState.loadingOlderComments,
    pins: pinsState.pins,
    pinsLoading: pinsState.pinsLoading,
    pinsErrorMessage: pinsState.pinsErrorMessage,
    sendingComment: chatState.sendingComment,
    replyPreview: chatState.replyTarget?.preview ?? null,
    jumpToCommentUuid: chatState.jumpToCommentUuid,
    showEditComment: chatState.editComment.showEditComment,
    editingCommentText: chatState.editComment.editingCommentText,
    isSavingComment: chatState.editComment.isSavingComment,
    scopePreset: chatState.scopePreset,
    activeContext: chatState.activeContext,
    handleOpenSlashCommand,
    loadOlderComments,
    reloadChat,
    handleSendComment,
    handleMessageAction,
    handlePinAction,
    handlePinClick,
    handleInsertUserMention,
    cancelReply,
    handleJumpToCommentHandled,
    closeEditComment,
    saveEditedComment,
    setEditingCommentText,
    handleScopePresetConsumed,
    closeActiveContext,
    scroll,
  };
}
